'use client';

import Image from 'next/image';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function BotoksunAlerjiRiski() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/botoksun-alerji-riski-gercek-tehlike-mi-yanlis-algi-mi';
  const pageTitle = 'Botoksun Alerji Riski: Gerçek Tehlike mi, Yanlış Algı mı?';
  const pageDescription = 'Botoks alerjisi gerçekten olur mu? Belirtileri, risk faktörleri ve alerji testinin önemi — bilimsel veriler ve klinik gözlemlerle açıklanıyor.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Botoksun Alerji Riski', href: '/tr/blog/botoksun-alerji-riski-gercek-tehlike-mi-yanlis-algi-mi' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Botoks alerji riski ve güvenlik değerlendirmesi"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-amber-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Güvenlik
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Botoksun Alerji Riski: Gerçek Tehlike mi, Yanlış Algı mı?
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>6 Mart 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>9 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Botoks (Botulinum toksini), hem estetik hem de nörolojik tedavilerde kullanılan, tıbbın en güçlü biyolojik ajanlarından biridir.
                Milyonlarca hasta üzerinde güvenle uygulanmasına rağmen, zaman zaman "botoksa alerjim var mı?" sorusu gündeme gelir.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Peki bu gerçekten bir alerji mi, yoksa yanlış yorumlanan fizyolojik bir tepkiden mi ibaret?
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣ Botoksun İçeriği
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Modern botoks preparatları yalnızca botulinum toksininden ibaret değildir.
                Her flakon şu bileşenleri içerir:
              </p>

              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-amber-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Bileşen</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Görev</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Botulinum Toksin Tip A</td>
                      <td className="border border-gray-300 px-4 py-3">Kas hareketini durduran ana etken madde</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Albumin (insan plazmasından)</td>
                      <td className="border border-gray-300 px-4 py-3">Toksini stabilize eder</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Sodyum klorür</td>
                      <td className="border border-gray-300 px-4 py-3">İzotonik denge sağlar</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Bu içeriklerin hiçbiri doğrudan "alerjen" değildir; ancak nadir durumlarda bağışıklık sistemi yanıtı tetiklenebilir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2️⃣ Gerçek Alerji Nedir?
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Alerji, bağışıklık sisteminin zararsız bir maddeyi düşman gibi algılayıp antikor üretmesi durumudur.
                Botoks sonrası olası alerjik tepkiler genellikle immünolojik değil, lokal irritasyon kaynaklıdır.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3️⃣ Alerji ile Karıştırılan Normal Reaksiyonlar
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-red-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Belirti</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Gerçek Alerji mi?</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Kızarıklık</td>
                      <td className="border border-gray-300 px-4 py-3">❌ Hayır</td>
                      <td className="border border-gray-300 px-4 py-3">Enjeksiyon travmasına bağlıdır</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Şişlik</td>
                      <td className="border border-gray-300 px-4 py-3">❌ Hayır</td>
                      <td className="border border-gray-300 px-4 py-3">Geçici ödemdir, 24 saatte azalır</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Kaşıntı</td>
                      <td className="border border-gray-300 px-4 py-3">🔸 Hafif alerjik duyarlılık olabilir</td>
                      <td className="border border-gray-300 px-4 py-3">6 saatte geçmezse takip gerekir</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Solunum sıkıntısı</td>
                      <td className="border border-gray-300 px-4 py-3">✅ Evet</td>
                      <td className="border border-gray-300 px-4 py-3">Nadir ama ciddi sistemik reaksiyon</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6">
                <p className="text-orange-800 font-medium">
                  📍 Gerçek alerji, her 10.000 uygulamada yaklaşık 1 vaka oranında görülür.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4️⃣ Bilimsel Veriler
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                FDA (2024) verilerine göre:
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-blue-800">
                  <li>Alerjik reaksiyon oranı: %0,01</li>
                  <li>Ciddi reaksiyon oranı (anafilaksi): %0,0005</li>
                  <li>En sık neden: Plazma kaynaklı albumine karşı duyarlılık</li>
                </ul>
                <p className="text-blue-800 mt-4">
                  Bu oran, yaygın kullanılan ağrı kesicilerin alerji oranından bile düşüktür.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5️⃣ Alerji Belirtileri Nasıl Fark Edilir?
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-green-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Belirti</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Başlama Süresi</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Hafif kızarıklık / kaşıntı</td>
                      <td className="border border-gray-300 px-4 py-3">1–2 saat</td>
                      <td className="border border-gray-300 px-4 py-3">Lokal reaksiyon, genellikle kendiliğinden geçer</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Yüzde sıcaklık hissi</td>
                      <td className="border border-gray-300 px-4 py-3">2–6 saat</td>
                      <td className="border border-gray-300 px-4 py-3">Damar genişlemesine bağlı</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Göz kapağında ödem</td>
                      <td className="border border-gray-300 px-4 py-3">12–24 saat</td>
                      <td className="border border-gray-300 px-4 py-3">Bölgesel hassasiyet, antihistaminik ile düzelir</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Yaygın döküntü / nefes darlığı</td>
                      <td className="border border-gray-300 px-4 py-3">Dakikalar içinde</td>
                      <td className="border border-gray-300 px-4 py-3">Acil müdahale gerekir</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6️⃣ Kimlerde Risk Daha Fazla?
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-purple-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Durum</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Risk Açıklaması</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Kronik alerjisi olanlar (toz, polen, ilaç)</td>
                      <td className="border border-gray-300 px-4 py-3">Vücut genel olarak daha reaktif</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Otoimmün hastalar</td>
                      <td className="border border-gray-300 px-4 py-3">Bağışıklık sistemi aşırı aktif olabilir</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Hamile / emziren kadınlar</td>
                      <td className="border border-gray-300 px-4 py-3">Önerilmez (etik ve güvenlik nedeniyle)</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Kas gevşetici ilaç kullananlar</td>
                      <td className="border border-gray-300 px-4 py-3">Sinir-kas iletiminde çakışma riski</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7️⃣ Klinik Gözlem
              </h2>
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
                <p className="text-blue-800 italic">
                  "Yıllardır binlerce hastaya botoks uyguladım.
                  Gerçek anlamda alerjik reaksiyon yalnızca bir kez gördüm; o da geçmişte plazma ürünlerine karşı alerjisi olan bir hastaydı.
                  Doğru hasta seçimi ve steril çalışma bu riski sıfıra yakın hale getirir."
                </p>
                <p className="text-blue-800 font-medium mt-4">
                  — Dr. Barış Çin, Estetik Cerrahi Uzmanı
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                8️⃣ Botoksa Alerji Testi Yapılır mı?
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Evet. Şüpheli hastalarda intradermal test yapılabilir.
                Kolun iç yüzüne çok küçük bir botoks dozu enjekte edilir.
                15 dakika içinde kızarıklık veya kabarıklık gelişirse, alerjik eğilim olduğu düşünülebilir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                9️⃣ Alerji Durumunda Ne Yapılır?
              </h2>
              
              <div className="bg-yellow-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-yellow-800">
                  <li>Hafif reaksiyonlarda: antihistaminik veya soğuk kompres</li>
                  <li>Orta düzey reaksiyonlarda: kortikosteroid enjeksiyonu</li>
                  <li>Şiddetli sistemik reaksiyonlarda: adrenalin (epinefrin) ve acil gözlem</li>
                </ul>
              </div>

              <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 mb-6">
                <p className="text-indigo-800 font-medium">
                  💬 Unutmayın: Gerçek alerji acildir, ama genellikle çok nadirdir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                🔟 Botoksun Alerjik Olmadığı Durumlar
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Bazen hastalar "botokstan sonra başım ağrıdı" veya "kaşım düşük oldu" gibi yanıtlar verir.
                Bu belirtiler alerji değil, botoksun kas fizyolojisiyle uyum sürecidir.
                Alerjiden tamamen farklı mekanizmalardır.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣1️⃣ Etik ve Klinik Değerlendirme
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Her medikal işlemde olduğu gibi, botoksta da risk vardır; ancak bu risk, bilimsel önlemlerle minimize edilebilir.
                Gerçek alerji riski %0,01'in altındadır.
                Bilinçli hasta ve dikkatli hekim iş birliği, bu oranı sıfıra yaklaştırır.
              </p>

              <div className="bg-purple-50 border-l-4 border-purple-400 p-4 mb-6">
                <p className="text-purple-800 italic">
                  "Estetikte güven, etkiden daha değerlidir."
                </p>
                <p className="text-purple-800 font-medium mt-2">
                  — Dr. Barış Çin
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣2️⃣ Sık Sorulan Sorular (FAQ)
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">1. Botoksa alerjim olabilir mi?</h3>
                  <p className="text-gray-600">Çok nadir. Genellikle geçmişte plazma veya ilaç alerjisi olan kişilerde görülebilir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2. Alerji testi yapılmalı mı?</h3>
                  <p className="text-gray-600">Şüpheli vakalarda evet. Özellikle ilk kez yaptıran ve alerjik bünyesi olan hastalarda.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">3. Alerji olursa etkisi kalıcı olur mu?</h3>
                  <p className="text-gray-600">Hayır, müdahale edilirse tamamen geçicidir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">4. Daha güvenli markalar var mı?</h3>
                  <p className="text-gray-600">FDA onaylı markalar (Allergan, Xeomin, Dysport) yüksek saflık oranına sahiptir.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">5. Alerji belirtileri ne kadar sürer?</h3>
                  <p className="text-gray-600">Hafif reaksiyonlar 24-48 saat, ciddi reaksiyonlar acil müdahale gerektirir.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">6. Botoks yaptırmadan önce ne yapmalıyım?</h3>
                  <p className="text-gray-600">Geçmiş alerji öykünüzü hekiminizle paylaşın ve gerekirse alerji testi yaptırın.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sonuç ve Klinik Öneri
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botoks, güvenli bir tıbbi işlemdir; alerjik reaksiyonlar istisnadır.
                Ancak her zaman dikkatli değerlendirme, steril çalışma ve bilgilendirme esastır.
              </p>

              <div className="bg-red-50 border-l-4 border-red-400 p-6 mb-6">
                <p className="text-red-800 font-medium">
                  🩺 Dr. Barış Çin öneriyor:
                </p>
                <p className="text-red-800 mt-2">
                  "Her hasta benzersizdir. Eğer vücudun geçmişte alerjik bir tepkisi olmuşsa, bunu hekiminle paylaş.
                  Bilim, önceden önlem alındığında alerjiyi sıfıra indirir."
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>FDA (2024) – "Safety and Hypersensitivity Reactions to Botulinum Toxin"</li>
                  <li>ISAPS Global Report (2024) – "Incidence of Allergic Reactions in Botulinum Procedures"</li>
                  <li>Harvard Health (2023) – "Understanding True vs. False Allergies"</li>
                  <li>Türk Estetik Tıp Derneği (TETD) – "Botoks Uygulamalarında Alerji Yönetimi"</li>
                </ul>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#botoks</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#alerji</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#DrBarışÇin</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#estetik</span>
                <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm">#klinikgüvenlik</span>
              </div>

              {/* Social Share Buttons */}
              <div className="border-t pt-8 mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Bu yazıyı paylaş:</h3>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaFacebookF className="w-4 h-4" />
                    <span>Facebook</span>
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="flex items-center space-x-2 bg-blue-400 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <FaTwitter className="w-4 h-4" />
                    <span>Twitter</span>
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="flex items-center space-x-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors"
                  >
                    <FaLinkedinIn className="w-4 h-4" />
                    <span>LinkedIn</span>
                  </button>
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <FaWhatsapp className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-amber-600 to-orange-700 rounded-3xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Botoks Uygulaması ve Güvenlik Danışmanlığı</h3>
          <p className="mb-6">Uzman doktorumuzdan botoks uygulaması ve alerji riski hakkında detaylı bilgi alın.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`https://wa.me/905438999586?text=${encodeURIComponent('Botoks uygulaması ve alerji riski hakkında bilgi almak istiyorum.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
            >
              <FaWhatsapp className="w-5 h-5 mr-2" />
              WhatsApp ile İletişim
            </a>
            <a
              href="/tr/iletisim"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-amber-600 rounded-full hover:bg-gray-100 transition-colors"
            >
              Randevu Al
            </a>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <a
              href="/tr/blog/botoks-nedir-nasil-yapilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Nedir, Nasıl Yapılır?</h4>
              <p className="text-gray-600 text-sm">Botoksun ne olduğu, nasıl çalıştığı ve uygulama süreci hakkında detaylı bilgiler.</p>
            </a>
            <a
              href="/tr/blog/botoks-fiyatlari-2026"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Fiyatları 2026</h4>
              <p className="text-gray-600 text-sm">Güncel botoks fiyatları ve nelere göre değiştiği hakkında detaylı bilgiler.</p>
            </a>
            <a
              href="/tr/blog/orijinal-botoks-anlama-yontemleri-guvenli-urun-tespit-etme"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Orijinal Botoksu Anlama Yöntemleri</h4>
              <p className="text-gray-600 text-sm">Orijinal botoks ürünlerini nasıl anlayacağınız ve güvenli ürün tespit etme yöntemleri.</p>
            </a>
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-gray-600 mb-4">
                Daha fazla bilgilendirici içerik için bizi takip edin.
              </p>
              <a
                href="/tr/blog"
                className="inline-flex items-center px-4 py-2 bg-amber-600 text-white rounded-full hover:bg-amber-700 transition-colors text-sm"
              >
                Tüm Blog Yazılarını Görüntüle
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
